import { CallSplitOutlined as CallSplitOutlinedIcon } from 'components/icons';
import { useMemo } from 'react';

import { FormattedDate } from 'components/misc/FormattedDate';
import PartyName from 'components/party/PartyName';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { FilterConfig } from 'components/tables/list-table/ListTableTypes';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters';
import { useLazyGetOutBoundReferralsWithFiltersQuery } from 'store/api/referals';
import { OutBoundReferralItem } from 'types/referrals';
import { t } from 'utils/getTranslations';

const OutBoundReferralsTable = ({
    passiveFilter = '',
    dataTableClassName,
}: {
    passiveFilter?: string;
    dataTableClassName?: string;
}): JSX.Element => {
    const [fetchData, { data, isFetching }] = useLazyGetOutBoundReferralsWithFiltersQuery();

    const columns = useMemo<DataTableColumnDef<OutBoundReferralItem>[]>(
        () => [
            {
                header: t('referrals.out-bound-table.client'),
                accessorKey: 'referredParty',
                enableSorting: false,
                meta: {
                    widthWeight: 3,
                    minPx: 220,
                },
                cell: ({ cell }) => {
                    const { referredParty } = cell.row.original;
                    return referredParty?.id ? (
                        <PartyName
                            party={referredParty}
                            isLink
                            useLinkWithOrigin
                            variant="list"
                            className="whitespace-normal"
                        />
                    ) : null;
                },
            },
            {
                header: t('referrals.out-bound-table.by-person'),
                accessorKey: 'personParty',
                enableSorting: false,
                meta: {
                    widthWeight: 3,
                    minPx: 220,
                },
                cell: ({ cell }) => {
                    const { personParty } = cell.row.original;
                    return personParty?.id ? (
                        <PartyName party={personParty} isLink useLinkWithOrigin variant="list" />
                    ) : null;
                },
            },
            {
                header: t('referrals.out-bound-table.total-cases'),
                accessorKey: 'matterCount',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 100,
                },
                cell: ({ getValue }) => getValue<number>() ?? null,
            },
            {
                header: t('referrals.out-bound-table.last-case'),
                accessorKey: 'lastMatterOpenedDate',
                enableSorting: false,
                meta: {
                    widthWeight: 3,
                    minPx: 160,
                },
                cell: ({ getValue }) => {
                    const value = getValue<Date | undefined>();
                    return value ? <FormattedDate value={value} fallback="" /> : null;
                },
            },
        ],
        []
    );

    const filterConfig: FilterConfig = useMemo(
        () => ({
            quickFilters: [],
            filters: [],
            searchBarSelectors: [{ selector: 'referredParty.name' }],
            passiveFilter: passiveFilter,
        }),
        [passiveFilter]
    );

    return (
        <ListTableWithFilters<OutBoundReferralItem>
            // Managed v8 engine for the weighted-width layout; column picker / reorder / resize
            // stay OFF (flags default false) — this table never had them.
            columnConfigId="outbound-referrals"
            columns={columns}
            data={data}
            isLoading={isFetching}
            pluralTextId="referrals.referrals"
            singularTextId="referrals.referral"
            noEntriesId="referrals.no-referrals-found"
            noEntriesBodyId="referrals.empty.body"
            noEntriesIcon={CallSplitOutlinedIcon}
            fetchData={fetchData}
            filterConfig={filterConfig}
            rowsPerPage={50}
            dataTableClassName={dataTableClassName}
            canRememberFilters={false}
        />
    );
};

export default OutBoundReferralsTable;
